import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Sparkles, TrendingUp, Users, Calendar, Building2, Zap, SlidersHorizontal, ChevronDown, User } from 'lucide-react';
import PageTemplate from '../../components/Common/PageTemplate';
import projectsDataTR from '../../data/ongoingProjects.tr.json';
import projectsDataEN from '../../data/ongoingProjects.en.json';

// Avesis user ID mapping - Add more as needed
const avesisUserIds = {
  'MURAT YILMAZ': '5934',
  'İBRAHİM ÇAĞATAY KARAASLAN': '6418',
  'ÇAĞATAY TAVŞANOĞLU': '6202',
  'SİBEL KÜÇÜKYILDIRIM ÇELİK': '6294',
  'HANİFE GÜLER DÖNMEZ': '7495',
  'İSMAİL ASLAN': '12320',
  'DERYA ALTINTAN': '100053',
  'BANU ŞEBNEM ÖNDER': '6666',
  'FATİH MANGIT': '5107',
  'BURCU TARIKAHYA HACIOĞLU': '13126',
  'SEMA YAYLA': '6645',
  'ALİ ASLAN DÖNMEZ': '6298',
  'BENGİ ÖZGÜN ÖZTÜRK': '7496',
  'ÖZGE ERİŞİZ KASAP': '7080',
  'NURAY ŞATIROĞLU': '6440',
  'İSMET YURDUŞEN': '7241',
  'ERGİ DENİZ ÖZSOY': '5857',
  'UĞUR BOZKAYA': '11169',
  'SULTAN EYLEM TOKSOY': '11461',
  'MURAT ŞEN': '6144',
  'GÜZİN EMECEN': '6001',
  'MEHMET ATAKAY': '5828',
  'ESRA BİRBEN': '5919',
  'NERMİN HANDE AVCIOĞLU': '7016',
  // Add more coordinator -> user ID mappings here
};

// Coordinator Avatar Component
const CoordinatorAvatar = ({ name }) => {
  const [imageError, setImageError] = useState(false);
  const userId = avesisUserIds[name];

  // Get initials from name
  const getInitials = (fullName) => {
    if (!fullName) return '?';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Generate color from name for consistent colors
  const getColorFromName = (name) => {
    const colors = [
      'bg-blue-500', 'bg-violet-500', 'bg-emerald-500',
      'bg-amber-500', 'bg-cyan-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-rose-500', 'bg-teal-500'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (userId && !imageError) {
    return (
      <div className="relative w-12 h-12 rounded-full overflow-hidden ring-2 ring-slate-200 shrink-0">
        <img
          src={`https://avesis.hacettepe.edu.tr/user/image/${userId}`}
          alt={name}
          onError={() => setImageError(true)}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div className={`w-12 h-12 rounded-full ${getColorFromName(name)} flex items-center justify-center ring-2 ring-slate-200 shrink-0`}>
      <span className="text-white font-bold text-sm">{getInitials(name)}</span>
    </div>
  );
};

const DevamEdenProjeler = () => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeGroup, setActiveGroup] = useState('all');
  const [activeType, setActiveType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [displayCount, setDisplayCount] = useState(10);

  // Select data based on current language
  const projectsData = i18n.language === 'tr' ? projectsDataTR : projectsDataEN;

  const groups = useMemo(() => {
    const uniqueGroups = [...new Set(projectsData.filter(p => p.group).map(p => p.group))];
    return ['all', ...uniqueGroups.sort()];
  }, []);

  const types = useMemo(() => {
    const uniqueTypes = [...new Set(projectsData.filter(p => p.type).map(p => p.type))];
    return ['all', ...uniqueTypes.sort()];
  }, []);

  const filteredProjects = useMemo(() => {
    setDisplayCount(10); // Reset display count when filters change
    return projectsData.filter(project => {
      const matchesSearch =
        project.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.coordinator?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.projectNo?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesGroup = activeGroup === 'all' || project.group === activeGroup;
      const matchesType = activeType === 'all' || project.type === activeType;
      return matchesSearch && matchesGroup && matchesType;
    });
  }, [searchTerm, activeGroup, activeType, projectsData]);

  const stats = useMemo(() => ({
    total: projectsData.length,
    active: projectsData.filter(p => p.status === 'Yürürlükte' || p.status === 'Active').length,
    researchers: new Set(projectsData.map(p => p.coordinator)).size
  }), [projectsData]);

  const groupConfig = {
    'KBAG': { color: '#3B82F6', bg: 'bg-blue-500', ring: 'ring-blue-500/20' },
    'MFAG': { color: '#8B5CF6', bg: 'bg-violet-500', ring: 'ring-violet-500/20' },
    'SBAG': { color: '#10B981', bg: 'bg-emerald-500', ring: 'ring-emerald-500/20' },
    'TOVAG': { color: '#F59E0B', bg: 'bg-amber-500', ring: 'ring-amber-500/20' },
    'ÇAYDAG': { color: '#06B6D4', bg: 'bg-cyan-500', ring: 'ring-cyan-500/20' },
    'KAMAG': { color: '#EF4444', bg: 'bg-red-500', ring: 'ring-red-500/20' },
    'TEYDEB': { color: '#F97316', bg: 'bg-orange-500', ring: 'ring-orange-500/20' },
    'TÜSEB': { color: '#EC4899', bg: 'bg-pink-500', ring: 'ring-pink-500/20' },
    'AB': { color: '#6366F1', bg: 'bg-indigo-500', ring: 'ring-indigo-500/20' }
  };

  return (
    <PageTemplate
      title={t('pages.ongoingProjects.title')}
      subtitle={t('pages.ongoingProjects.subtitle')}
    >
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          {/* Hero Stats - Compact Modern Design */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10"
          >
            {/* Total Projects Card */}
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 p-5 shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
            >
              {/* Animated Background Blob */}
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-500"></div>

              {/* Content */}
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-4xl font-black text-white tracking-tight drop-shadow-md mb-1">
                    {stats.total}
                  </h3>
                  <p className="text-blue-50 font-medium text-sm">{t('pages.ongoingProjects.totalProjects')}</p>
                </div>

                {/* Icon */}
                <div className="relative">
                  <div className="absolute inset-0 bg-white rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
                  <div className="relative bg-white/20 backdrop-blur-sm p-3 rounded-xl ring-1 ring-white/30 group-hover:scale-110 transition-transform duration-300">
                    <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                </div>
              </div>

              {/* Border */}
              <div className="absolute inset-0 rounded-2xl ring-1 ring-white/20 group-hover:ring-white/40 transition-all duration-300"></div>
            </motion.div>

            {/* Active Projects Card */}
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 p-5 shadow-lg hover:shadow-emerald-500/25 transition-all duration-300"
            >
              {/* Animated Background Blob */}
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-500"></div>

              {/* Content */}
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-4xl font-black text-white tracking-tight drop-shadow-md">
                      {stats.active}
                    </h3>
                    {/* Pulse Indicator */}
                    <div className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                    </div>
                  </div>
                  <p className="text-emerald-50 font-medium text-sm">{t('pages.ongoingProjects.activeProjects')}</p>
                </div>

                {/* Icon */}
                <div className="relative">
                  <div className="absolute inset-0 bg-white rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
                  <div className="relative bg-white/20 backdrop-blur-sm p-3 rounded-xl ring-1 ring-white/30 group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                </div>
              </div>

              {/* Border */}
              <div className="absolute inset-0 rounded-2xl ring-1 ring-white/20 group-hover:ring-white/40 transition-all duration-300"></div>
            </motion.div>

            {/* Researchers Card */}
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 p-5 shadow-lg hover:shadow-violet-500/25 transition-all duration-300"
            >
              {/* Animated Background Blob */}
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-500"></div>

              {/* Content */}
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-4xl font-black text-white tracking-tight drop-shadow-md mb-1">
                    {stats.researchers}
                  </h3>
                  <p className="text-violet-50 font-medium text-sm">{t('pages.ongoingProjects.researchers')}</p>
                </div>

                {/* Icon */}
                <div className="relative">
                  <div className="absolute inset-0 bg-white rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
                  <div className="relative bg-white/20 backdrop-blur-sm p-3 rounded-xl ring-1 ring-white/30 group-hover:scale-110 transition-transform duration-300">
                    <Users className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                </div>
              </div>

              {/* Border */}
              <div className="absolute inset-0 rounded-2xl ring-1 ring-white/20 group-hover:ring-white/40 transition-all duration-300"></div>
            </motion.div>
          </motion.div>

          {/* Search & Filter - Modern Pill Design */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 space-y-6"
          >
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('pages.ongoingProjects.searchPlaceholder')}
                className="w-full pl-14 pr-6 py-5 bg-white rounded-full border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-slate-900 placeholder:text-slate-400 font-medium shadow-sm"
              />
            </div>

            {/* Filter Toggle Button */}
            <div className="flex justify-center">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
              >
                <SlidersHorizontal className="w-5 h-5" />
                {t('pages.ongoingProjects.filters')}
                {(activeGroup !== 'all' || activeType !== 'all') && (
                  <span className="px-2 py-0.5 bg-white text-slate-900 rounded-full text-xs font-bold">
                    {[activeGroup !== 'all' ? 1 : 0, activeType !== 'all' ? 1 : 0].reduce((a, b) => a + b, 0)}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Filters */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden space-y-6 pt-2"
                >
                  {/* Group Filter Pills */}
                  <div className="space-y-3">
              <p className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {t('pages.ongoingProjects.projectGroup')}
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {groups.map(group => {
                  const config = groupConfig[group];
                  const isActive = activeGroup === group;

                  if (group === 'all') {
                    return (
                      <button
                        key="all"
                        onClick={() => setActiveGroup('all')}
                        className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all ${
                          isActive
                            ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20'
                            : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                        }`}
                      >
                        {t('pages.ongoingProjects.all')}
                      </button>
                    );
                  }

                  return (
                    <button
                      key={group}
                      onClick={() => setActiveGroup(group)}
                      className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all ${
                        isActive
                          ? `${config.bg} text-white shadow-lg ring-4 ${config.ring}`
                          : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                      }`}
                    >
                      {group}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Type Filter Pills */}
            <div className="space-y-3">
              <p className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {t('pages.ongoingProjects.projectType')}
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {types.map(type => {
                  const isActive = activeType === type;

                  if (type === 'all') {
                    return (
                      <button
                        key="all"
                        onClick={() => setActiveType('all')}
                        className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all ${
                          isActive
                            ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20'
                            : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                        }`}
                      >
                        {t('pages.ongoingProjects.all')}
                      </button>
                    );
                  }

                  return (
                    <button
                      key={type}
                      onClick={() => setActiveType(type)}
                      className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all ${
                        isActive
                          ? 'bg-slate-700 text-white shadow-lg ring-4 ring-slate-700/20'
                          : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                      }`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Results Count */}
            <div className="text-center">
              <p className="text-slate-600">
                <span className="font-bold text-slate-900">{filteredProjects.length}</span> {t('pages.ongoingProjects.projects')}
              </p>
            </div>
          </motion.div>

          {/* Projects Grid - Card Design 2025 */}
          <div className="grid grid-cols-1 gap-4 md:gap-6">
            {filteredProjects.slice(0, displayCount).map((project, idx) => {
              const config = groupConfig[project.group] || { color: '#64748B', bg: 'bg-slate-500', ring: 'ring-slate-500/20' };

              return (
                <motion.article
                  key={project.projectNo}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="group relative bg-white rounded-3xl p-4 md:p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 overflow-hidden"
                >
                  {/* Decorative Element */}
                  <div
                    className="absolute top-0 left-0 w-1 h-full transition-all duration-500 group-hover:w-2"
                    style={{ backgroundColor: config.color }}
                  ></div>

                  {/* Header */}
                  <div className="flex flex-wrap items-start justify-between gap-2 md:gap-4 mb-4 md:mb-6 ml-3 md:ml-6">
                    <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                      {/* Group Badge */}
                      <span
                        className={`inline-flex items-center gap-1 md:gap-2 px-2 md:px-4 py-1 md:py-2 ${config.bg} text-white rounded-full text-xs font-bold tracking-wide`}
                      >
                        <Zap className="w-3 h-3 md:w-3.5 md:h-3.5" />
                        {project.group}
                      </span>

                      {/* Type Badge */}
                      {project.type && (
                        <span className="px-2 md:px-4 py-1 md:py-2 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold">
                          {project.type}
                        </span>
                      )}

                      {/* Status */}
                      {(project.status === 'Yürürlükte' || project.status === 'Active') && (
                        <span className="inline-flex items-center gap-1 md:gap-2 px-2 md:px-4 py-1 md:py-2 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                          </span>
                          <span className="hidden sm:inline">{t('pages.ongoingProjects.active')}</span>
                        </span>
                      )}
                    </div>

                    {/* Project Number */}
                    <div className="font-mono font-bold text-slate-400 text-xs md:text-sm">
                      #{project.projectNo}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-base md:text-2xl font-bold text-slate-900 mb-4 md:mb-8 leading-snug md:leading-tight ml-3 md:ml-6 group-hover:text-blue-600 transition-colors">
                    {project.projectName}
                  </h3>

                  {/* Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 ml-3 md:ml-6">
                    {/* Coordinator */}
                    <div className="flex items-center gap-3 md:gap-4">
                      <div className="hidden sm:block">
                        <CoordinatorAvatar name={project.coordinator} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                          {t('pages.ongoingProjects.coordinator')}
                        </p>
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {project.coordinator}
                        </p>
                      </div>
                    </div>

                    {/* Institution */}
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="p-2 md:p-3 bg-slate-50 rounded-2xl shrink-0">
                        <Building2 className="w-4 h-4 md:w-5 md:h-5 text-slate-600" strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                          {t('pages.ongoingProjects.institution')}
                        </p>
                        <p className="text-xs md:text-sm font-semibold text-slate-900 truncate">
                          {project.institution}
                        </p>
                      </div>
                    </div>

                    {/* Date */}
                    {project.startDate && (
                      <div className="flex items-start gap-3 md:gap-4">
                        <div className="p-2 md:p-3 bg-slate-50 rounded-2xl shrink-0">
                          <Calendar className="w-4 h-4 md:w-5 md:h-5 text-slate-600" strokeWidth={2} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                            {t('pages.ongoingProjects.startDate')}
                          </p>
                          <p className="text-xs md:text-sm font-semibold text-slate-900">
                            {project.startDate}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Hover Effect Line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </motion.article>
              );
            })}
          </div>

          {/* Show More / Show Less Button */}
          {filteredProjects.length > 10 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-center mt-12"
            >
              <button
                onClick={() => setDisplayCount(displayCount === 10 ? filteredProjects.length : 10)}
                className="px-8 py-4 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 hover:shadow-xl"
              >
                {displayCount === 10 ? t('pages.ongoingProjects.showMore') : t('pages.ongoingProjects.showLess')}
              </button>
            </motion.div>
          )}

          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-24"
            >
              <div className="inline-flex p-6 bg-slate-100 rounded-full mb-6">
                <Search className="w-12 h-12 text-slate-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('pages.ongoingProjects.noProjectsFound')}</h3>
              <p className="text-slate-600 mb-6">{t('pages.ongoingProjects.noProjectsDescription')}</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setActiveGroup('all');
                  setActiveType('all');
                }}
                className="px-8 py-4 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
              >
                {t('pages.ongoingProjects.reset')}
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </PageTemplate>
  );
};

export default DevamEdenProjeler;
